import { Button }                   from '@/components/ui/button';
import React                        from 'react';
import { Dispatch }                 from 'react';
import { Field }                    from '@/components/ui/field';
import { Box }                      from '@chakra-ui/react';
import { Grid }                     from '@chakra-ui/react';
import { GridItem }                 from '@chakra-ui/react';
import { Input }                    from '@chakra-ui/react';
import { NativeSelect }             from "@chakra-ui/react"
import { SetStateAction }           from 'react';
import { Stack }                    from '@chakra-ui/react';
import { Text }                     from '@chakra-ui/react';
import { toaster }                  from '@/components/ui/toaster';
import { useForm }                  from 'react-hook-form';
import { useTranslation }           from 'react-i18next';
import ContactModel                 from '@/models/services/network/ContactModel';
import useApiCollection             from '@widesk-core/hooks/useApiCollection';
import TypeModel                    from '@/models/services/file/TypeModel';
import Image                        from '@/components/Image/Image';
import FileModel                    from '@/models/services/file/FileModel';
import CloseIcon                    from '@/assets/icons/CloseIcon';
import _capitalize                  from "lodash/capitalize";
import useFetch                     from '@widesk-core/hooks/useFetch';
import CropperModal                 from '@/components/modal/CropperModal';
import getTextAndTranslateFromError from '@widesk-core/tools/getTextAndTranslateFromError';
import { ModalData }                from '@/components/modal/Modal';

interface IContactFormProps {
	contact?: ContactModel;
	existingSource?: string;
	file?: FileModel;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	setModalData: (value: React.SetStateAction<ModalData | undefined>) => void;
	onSuccess: () => void;
}

interface FormValues {
	email: string;
	firstname: string;
	gender: string;
	lastname: string;
}

const ContactForm = (props: IContactFormProps) => {
	const { contact, existingSource, file, setIsOpen, setModalData, onSuccess } = props;
	const { t } =  useTranslation();
	const [selectedGender, setSelectedGender] = React.useState('1');
	const [nameUppercase, setNameUppercase] = React.useState('');

	const [newProfilePicture, setNewProfilePicture] = React.useState<File | undefined>();
	const [imageSource, setImageSource] = React.useState<string>('');
	const [cropperImageSource, setCropperImageSource] = React.useState<string>('');
	const typeCollection = useApiCollection(TypeModel);
	const [cropperModalIsOpen, setCropperModalIsOpen] = React.useState(false);

	useFetch(async () => {
		await typeCollection.list();
	}, true);

	const {
		formState: { errors, isSubmitting },
		handleSubmit,
		register,
		setValue,
	} = useForm<FormValues>()
	
	const onSubmit = handleSubmit(async (data) => {
		const { email, firstname, lastname } = data;
		try {
			const newContact = contact?.id ? contact : new ContactModel();
			await newContact.patch({
				email: email,
				firstname: firstname,
				gender: parseInt(selectedGender),
				lastname: lastname,
			});

			if (existingSource && imageSource === '' && file) {
				await file.destroy();
			} else if (newProfilePicture instanceof File) {

				const newFile = new FileModel();
				const type = typeCollection.find(m => m.reference === 'contact_photo') as TypeModel;

				await newFile.patchFormData({
					type: type.urn,
					sourceUrn: newContact.urn,
					file: newProfilePicture,
				});
			}

			toaster.create({
				title: t('pages.contacts.forms.contactForm.success', { action: contact?.id ? t('pages.contacts.list.row.actions.edited.label').toLowerCase() : t('pages.contacts.actions.added').toLowerCase() }),
				type: 'success'
			});
			onSuccess();
			setModalData(undefined);
			setIsOpen(false);

		} catch (error) {
			toaster.create({
				title: `${t('pages.contacts.forms.contactForm.errors.submit')} : ${getTextAndTranslateFromError(error)}`,
				type: 'error',
				duration: 5000,
			});
			setModalData(undefined);
			setIsOpen(false);
		}
	})

	React.useEffect(() => {
		if (contact?.id) {
			setValue('firstname', contact.firstname || '');
			setValue('lastname', contact.lastname || '');
			setValue('email', contact.email || '');
			setValue('gender', contact.gender?.toString() || '1');
			setNameUppercase(contact.lastname.toUpperCase() || '');
			setSelectedGender(contact.gender?.toString() || '1');
		}
		if (existingSource) {
			setImageSource(existingSource);
		}
	}, []);

	React.useEffect(() => {
		if (newProfilePicture) {
			const newSource = URL.createObjectURL(newProfilePicture);
			setImageSource(newSource);
		}
	}, [newProfilePicture]);

	return (
		<form onSubmit={onSubmit}>
			<CropperModal
				imageSource={cropperImageSource} 
				imageType={newProfilePicture?.type || ''} 
				isOpen={cropperModalIsOpen} 
				setIsOpen={setCropperModalIsOpen}
				onSubmit={async (file) => { setNewProfilePicture(file); }}
				onError={() => {
					toaster.create({
						title: t('pages.personalInformation.personalInformationBox.modals.cropper.messages.errors.error'),
						type: 'error'
					});
				}}
			/>
			<Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(2, 1fr)" }}>	
				<GridItem order={{ base: 1, lg: -1 }}>
					<Stack gap="20px" marginTop="20px">
						<Field
							label={
								<Text 
									className="poppins-semibold" 
									fontSize="14px" 
									fontWeight="bold"
								>
									{t('pages.contacts.forms.contactForm.firstname')}
								</Text>
							}
							invalid={!!errors.firstname}
							required
						>
							<Input
								placeholder={t('pages.contacts.forms.contactForm.firstname')}
								{...register('firstname', { required: true })}
							/>
						</Field>
						<Field
							label={
								<Text 
									className="poppins-semibold" 
									fontSize="14px" 
									fontWeight="bold"
								>
									{t('pages.contacts.forms.contactForm.lastname')}
								</Text>
							}
							invalid={!!errors.lastname}
							required
						>
							<Input
								value={nameUppercase}
								placeholder={t('pages.contacts.forms.contactForm.lastname')}
								{...register('lastname', { required: true })}
								onChange={event => {
									const { value } = event.target;
									setNameUppercase(value.toUpperCase());
								}}
							/>
						</Field>
						<Field
							label={
								<Text 
									className="poppins-semibold" 
									fontSize="14px" 
									fontWeight="bold"
								>
									{t('pages.contacts.forms.contactForm.email')}
								</Text>
							}
							invalid={!!errors.email}
							required
						>
							<Input
								placeholder={t('pages.contacts.forms.contactForm.email')}
								{...register('email', {
									pattern: {
										value: /^[a-z0-9]+([._-][a-z0-9]+)*@[a-z0-9]+([-][a-z0-9]+)*\.[a-z]{2,}$/,
										message: t('pages.contacts.forms.contactForm.errors.pattern'),
									},
								})}
							/>
							{errors.email?.type === 'pattern' && <Text color="red">{t('pages.contacts.forms.contactForm.errors.pattern')}</Text>}
						</Field>
						<Field
							label={
								<Text 
									className="poppins-semibold" 
									fontSize="14px" 
									fontWeight="bold"
								>
									{t('pages.contacts.forms.contactForm.gender')}
								</Text>
							}
							required
						>
							<NativeSelect.Root size="sm" width="240px">
								<NativeSelect.Field
									value={selectedGender}
									onChange={(e) => setSelectedGender(e.currentTarget.value)}
								>
									<option value="1">{_capitalize(t('app.selects.genders.man'))}</option>
									<option value="2">{_capitalize(t('app.selects.genders.woman'))}</option>
								</NativeSelect.Field>
								<NativeSelect.Indicator />
							</NativeSelect.Root>
						</Field>

					</Stack>
				</GridItem>
				<GridItem>
					<Box
						display="flex"
						justifyContent={{ lg: "center" }}
						marginTop={{ lg: "20px" }}
						marginLeft={{ lg: "40px" }}
					>
						<Box
							display="flex"
							gap={2}
						>
							<Box
								width="200px"
								height="200px"
							>
								<Image 
									actions={{
										edit: (file: File) => {
											const newSource = URL.createObjectURL(file);
											setCropperImageSource(newSource);
											setCropperModalIsOpen(true);
										},
										delete: () => {
											setNewProfilePicture(undefined);
											setImageSource('')
										},
									}}
									borderRadius="8px"
									errorSize='xl'
									src={imageSource}
									type='profile'
									height="200px"
									width="200px"
								/>
							</Box>
							<Box
								cursor="pointer"
								hideFrom="lg"
								hidden={!imageSource}
								onClick={() => {
									setNewProfilePicture(undefined);
									setImageSource('')
								}}
							>
								<CloseIcon size="lg" />
							</Box>
						</Box>
					</Box>
				</GridItem>
			</Grid>
			<Button 
				variant="solid" 
				type="submit" 
				width="100%"
				marginTop="30px"
				loading={isSubmitting || typeCollection.isLoading}
			>
				{t('pages.contacts.forms.contactForm.submit', { action: contact?.id ? t('pages.contacts.list.row.actions.edit.label') : t('pages.contacts.actions.add') })}
			</Button>
		</form>
	);
};

export default ContactForm;